import { SOCIAL_AD_TYPES } from "utils/constants";
import { PAC_FORM_TYPES } from "social/constants";
import { getNewClientID } from "utils/common";
import { tellSentry } from "utils/Rave";


function _getCreativeLevelValues(value){
	let {
		name = "",
		isDynamic,
		assetType,
	} = value;

	return {
		name,
		isDynamic,
		assetType,
	};
}

export function defaultDeserializer(values, adType) {
	const {
		socialValues = {},
	} = values;

	return {
		adType,
		[adType]: {
			..._getCreativeLevelValues(values),
			...socialValues,
		},
	};
}

export function deserializeFacebook(values, adType) {
	const {
		socialValues = {},
	} = values;

	const placements = socialValues.placements || [];
	const deserializedPlacements = placements.reduce((prev, placement) => {
		const { name } = placement.type;
		prev[name] = { ...placement };
		return prev;
	}, {});

	return {
		adType,
		[adType]: {
			..._getCreativeLevelValues(values),
			...socialValues,
			cta: socialValues.cta?.name,
			placements: deserializedPlacements,
			_currentActivePAC: PAC_FORM_TYPES.DEFAULT, // This field does not get sent to the API, but it is used for tracking the active placement within an adType
		},
	};
}

export function deserializeFacebookMO(values, adType){
	const {
		socialValues = {},
	} = values;

	const placements = socialValues.placements || [];
	const deserializedPlacements = placements.reduce((prev, placement) => {
		const { name } = placement.type;
		prev[name] = { ...placement };
		return prev;
	}, {});

	return {
		adType,
		[adType]: {
			..._getCreativeLevelValues(values),
			...socialValues,
			cta:{
				...socialValues.cta,
				value:socialValues.cta?.name,
			},
			// cta: socialValues.cta?.name,
			placements: deserializedPlacements,
			_currentActivePAC: PAC_FORM_TYPES.DEFAULT, // This field does not get sent to the API, but it is used for tracking the active placement within an adType
		},
	};
}

export function deserializeFacebookCarousel(values, adType) {
	const {
		socialValues = {},
	} = values;

	const serializedCards = socialValues.cards?.length ? socialValues.cards : [
		{
			type: "image",
			order: 0,
		},
		{
			type: "image",
			order: 1,
		}
	];

	const cards = (serializedCards).map((card) => {
		return {
			...card,
			clientId: getNewClientID(),
		};
	});

	return {
		adType,
		[adType]: {
			..._getCreativeLevelValues(values),
			...socialValues,
			cta: socialValues.cta?.name,
			currentCard: 0,
			cards,
		},
	};
}

export function deserializeFacebookCarouselMO(values, adType) {
	const {
		socialValues = {},
	} = values;

	const serializedCards = socialValues.cards?.length ? socialValues.cards : [
		{
			type: "image",
			order: 0,
		},
		{
			type: "image",
			order: 1,
		}
	];

	const cards = (serializedCards).map((card) => {
		return {
			...card,
			clientId: getNewClientID(),
		};
	});

	return {
		adType,
		[adType]: {
			..._getCreativeLevelValues(values),
			...socialValues,
			cta:{
				...socialValues.cta,
				value:socialValues.cta?.name,
			},
			currentCard: 0,
			cards,
		},
	};
}

export function deserializeGoogleDemandGenCarousel(values, adType, socialState) {
	const {
		socialValues = {},
		name = "",
	} = values;

	socialValues.requiredAspectRatios = (socialValues.aspectRatios || []).reduce((prev, ratio) => {
		prev[ratio] = true;
		return prev;
	}, {});

	delete socialValues.aspectRatios;

	const serializedCards = socialValues.cards?.length ? socialValues.cards : [
		{
			type: "image",
			order: 0,
		},
		{
			type: "image",
			order: 1,
		}
	];

	const cards = (serializedCards).map((card) => {
		const locale = card.cta?.locale || "en";
		const languageName = socialState?.googleLocales?.locales?.[locale]?.name || "English";
		const language = {
			id: locale,
			name: languageName,
			value: locale,
			label: languageName,
		};

		const formattedImages = (card.images || []).reduce((prev, image) => {
			const { aspectRatio, ...imageData } = image || {};
			try{
				prev[aspectRatio] = imageData;
			} catch {
				tellSentry("Google Demand Gen Carousel image has unknown or missing aspectRatio", aspectRatio);
			}

			return prev;
		}, {});

		return {
			...card,
			images: formattedImages,
			cta: card.cta?.name,
			language,
			clientId: getNewClientID(),
		};
	});

	return {
		adType,
		[adType]: {
			name,
			...socialValues,
			currentCard: 0,
			cards,
		},
	};
}

export function deserializePinterestCarousel(values, adType) {
	const {
		socialValues = {},
	} = values;

	const serializedCards = socialValues.cards?.length ? socialValues.cards : [
		{
			type: "image",
			order: 0,
		},
		{
			type: "image",
			order: 1,
		}
	];

	const cards = (serializedCards).map((card) => {
		return {
			...card,
			clientId: getNewClientID(),
		};
	});

	return {
		adType,
		[adType]: {
			..._getCreativeLevelValues(values),
			...socialValues,
			currentCard: 0,
			cards,
		},
	};
}

export function deserializeLinkedInCarousel(values, adType) {
	const {
		socialValues = {},
	} = values;

	const serializedCards = socialValues.cards?.length ? socialValues.cards : [
		{
			type: "image",
			order: 0,
		},
		{
			type: "image",
			order: 1,
		}
	];

	const cards = (serializedCards).map((card) => {
		return {
			...card,
			clientId: getNewClientID(),
		};
	});

	return {
		adType,
		[adType]: {
			..._getCreativeLevelValues(values),
			...socialValues,
			currentCard: 0,
			cards,
		},
	};
}

export function deserializeGoogleDemandGenImage(values, adType, socialState) {
	const {
		socialValues = {},
		name = "",
	} = values;

	function deserializeTextFieldArray(field) {
		if(field.length) {
			return field.map(val => val.name);
		}
		return [null];
	}

	const { headlines, descriptions, cta } = socialValues || {};
	const deseralizedHeadlines = deserializeTextFieldArray(headlines);
	const deserializedDescriptions = deserializeTextFieldArray(descriptions);

	const locale = cta?.locale || "en";
	const languageName = socialState?.googleLocales?.locales?.[locale]?.name || "English";
	const language = {
		id: locale,
		name: languageName,
		value: locale,
		label: languageName,
	};

	return {
		adType,
		[adType]: {
			name,
			...socialValues,
			language,
			cta: cta?.name,
			headlines: deseralizedHeadlines,
			descriptions: deserializedDescriptions,
		},
	};
}

const DESERIALIZER_BY_AD_TYPE = {
	[SOCIAL_AD_TYPES.FACEBOOK_IMAGE]: deserializeFacebook,
	[SOCIAL_AD_TYPES.FACEBOOK_VIDEO]: deserializeFacebook,
	[SOCIAL_AD_TYPES.FACEBOOK_CAROUSEL]: deserializeFacebookCarousel,
	[SOCIAL_AD_TYPES.FACEBOOK_IMAGE_MO]: deserializeFacebookMO,
	[SOCIAL_AD_TYPES.FACEBOOK_VIDEO_MO]: deserializeFacebookMO,
	[SOCIAL_AD_TYPES.FACEBOOK_CAROUSEL_MO]: deserializeFacebookCarouselMO,
	[SOCIAL_AD_TYPES.GOOGLE_DEMAND_GEN_CAROUSEL]: deserializeGoogleDemandGenCarousel,
	[SOCIAL_AD_TYPES.PINTEREST_CAROUSEL]: deserializePinterestCarousel,
	[SOCIAL_AD_TYPES.LINKEDIN_CAROUSEL]: deserializeLinkedInCarousel,
	[SOCIAL_AD_TYPES.GOOGLE_DEMAND_GEN_IMAGE]: deserializeGoogleDemandGenImage,
};

export function deserializeByAdType(values, adType, socialState = {}) {
	const deserializer = DESERIALIZER_BY_AD_TYPE[adType] || defaultDeserializer;
	return deserializer(values, adType, socialState);
}
